/**
 * Shared Tailwind class tables for the per-tag-color chip styling used by
 * `PlayerCard`, `OfficerManagementPanel`, and `GvgPlannerView`.
 *
 * Three components historically inlined their own `TAG_CHIP_BG` / `TAG_SWATCH_BG`
 * `Record<TagColor, string>` tables. The active/inactive variants used by the
 * toolbar's filter strip versus the passive chips on a `PlayerCard` drifted
 * slightly — this composable merges them into a single canonical mapping so a
 * future palette tweak only has to happen once.
 *
 * The class strings are composed at runtime (`bg-tag-${color}/20`, etc.) so the
 * Tailwind v4 JIT scanner cannot see them through template text. They ride on
 * the `@source inline(...)` safelist + `@theme` block in
 * `src/styles/tailwind.css`; the safelist test in
 * `src/styles/__tests__/tailwind.safelist.test.ts` pins the contract.
 */
import { TagColor } from '@/features/gvg-planner/types/board'

/**
 * Render the canonical default tag names with the compact in-game glyphs.
 * The stored names remain unchanged because they are shared backend seed and
 * reset contracts; this helper only changes what the planner displays.
 */
export function tagDisplayName(name: string): string {
  if (name === 'chicken') return '🐔'
  if (name === 'tree') return '🌴'
  return name.replace(/^forest(?=\s|$)/, '🪙')
}

/**
 * Options for `tagChipClassFor`. `active: true` mirrors the toolbar filter strip
 * (solid color background, dark text). `active: false` mirrors the passive tag
 * chips pinned to a player (translucent background, colored text + border).
 */
export interface TagChipStyleOptions {
  readonly color: TagColor
  readonly active: boolean
}

/**
 * Passive chip style: translucent color background, colored text + 30% border.
 * Matches the chips pinned to a `PlayerCard` (no toolbar filter involvement).
 */
const TAG_CHIP_BG_INACTIVE: Readonly<Record<TagColor, string>> = {
  [TagColor.Neutral]: 'bg-tag-neutral/20 text-tag-neutral border border-tag-neutral/30',
  [TagColor.Red]: 'bg-tag-red/20 text-tag-red border border-tag-red/30',
  [TagColor.Blue]: 'bg-tag-blue/20 text-tag-blue border border-tag-blue/30',
  [TagColor.Green]: 'bg-tag-green/20 text-tag-green border border-tag-green/30',
  [TagColor.Purple]: 'bg-tag-purple/20 text-tag-purple border border-tag-purple/30',
  [TagColor.Amber]: 'bg-tag-amber/20 text-tag-amber border border-tag-amber/30',
  [TagColor.Rose]: 'bg-tag-rose/20 text-tag-rose border border-tag-rose/30',
  [TagColor.Cyan]: 'bg-tag-cyan/20 text-tag-cyan border border-tag-cyan/30',
  [TagColor.Slate]: 'bg-tag-slate/20 text-tag-slate border border-tag-slate/30',
  [TagColor.Stone]: 'bg-tag-stone/20 text-tag-stone border border-tag-stone/30',
}

/**
 * Active chip style: solid color background, dark base-background text. Used
 * by the toolbar's filter strip when the matching tag filter is on.
 */
const TAG_CHIP_BG_ACTIVE: Readonly<Record<TagColor, string>> = {
  [TagColor.Neutral]: 'border border-current/30 bg-tag-neutral text-bg',
  [TagColor.Red]: 'border border-current/30 bg-tag-red text-bg',
  [TagColor.Blue]: 'border border-current/30 bg-tag-blue text-bg',
  [TagColor.Green]: 'border border-current/30 bg-tag-green text-bg',
  [TagColor.Purple]: 'border border-current/30 bg-tag-purple text-bg',
  [TagColor.Amber]: 'border border-current/30 bg-tag-amber text-bg',
  [TagColor.Rose]: 'border border-current/30 bg-tag-rose text-bg',
  [TagColor.Cyan]: 'border border-current/30 bg-tag-cyan text-bg',
  [TagColor.Slate]: 'border border-current/30 bg-tag-slate text-bg',
  [TagColor.Stone]: 'border border-current/30 bg-tag-stone text-bg',
}

/**
 * Solid color swatch used by the player-tag listbox popup's color dot and any
 * other place that needs an opaque color marker (no background tint, no text
 * utility).
 */
const TAG_SWATCH_BG: Readonly<Record<TagColor, string>> = {
  [TagColor.Neutral]: 'bg-tag-neutral',
  [TagColor.Red]: 'bg-tag-red',
  [TagColor.Blue]: 'bg-tag-blue',
  [TagColor.Green]: 'bg-tag-green',
  [TagColor.Purple]: 'bg-tag-purple',
  [TagColor.Amber]: 'bg-tag-amber',
  [TagColor.Rose]: 'bg-tag-rose',
  [TagColor.Cyan]: 'bg-tag-cyan',
  [TagColor.Slate]: 'bg-tag-slate',
  [TagColor.Stone]: 'bg-tag-stone',
}

/**
 * Resolve the Tailwind class string for a tag chip under the given visual
 * state. `active: true` returns the solid-background toolbar-filter variant;
 * `active: false` returns the passive pinned-chip variant.
 */
export function tagChipClassFor(opts: TagChipStyleOptions): string {
  return opts.active ? TAG_CHIP_BG_ACTIVE[opts.color] : TAG_CHIP_BG_INACTIVE[opts.color]
}

/**
 * Resolve the Tailwind class string for an opaque color swatch (the small
 * filled circle used inside the tag-picker listbox popup, etc.).
 */
export function tagSwatchClassFor(color: TagColor): string {
  return TAG_SWATCH_BG[color]
}
